import { Card } from '@stripe/stripe-js';

import { rootApi } from '../rootApi';
import { PageOptions, APIResponse, HTTPRequestType, PaginatedResponse } from '../api.types';
import {
  Product,
  Invoice,
  OrgFeature,
  Subscription,
  FeatureUsage,
  PaymentMethod,
  ActivateSubscriptionProps,
} from './billing.types';

export const billingApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    /* Products ------------------------------------------------------------ */

    getProducts: builder.query<Product[], void>({
      query: () => ({
        url: '/billing/system/products',
      }),
    }),

    /* Subscription -------------------------------------------------------- */

    getMySubscription: builder.query<Subscription, void>({
      query: () => ({
        url: '/billing/organizations/my/subscription',
      }),
      providesTags: ['Subscription'],
    }),

    activateSubscription: builder.mutation<APIResponse, ActivateSubscriptionProps>({
      query: (data) => ({
        url: '/billing/organizations/my/subscription',
        method: HTTPRequestType.POST,
        data,
      }),
      invalidatesTags: ['Invoices'],
    }),

    upgradeSubscription: builder.mutation<APIResponse, ActivateSubscriptionProps>({
      query: (data) => ({
        url: '/billing/organizations/my/subscription',
        method: HTTPRequestType.PUT,
        data,
      }),
      invalidatesTags: ['Invoices'],
    }),

    cancelSubscription: builder.mutation<APIResponse, { cancel_immediately: boolean }>({
      query: (data) => ({
        url: '/billing/organizations/my/subscription/cancel',
        method: HTTPRequestType.POST,
        data,
      }),
      invalidatesTags: ['Subscription'],
    }),

    /* Payments ------------------------------------------------------------ */

    getMyPaymentMethods: builder.query<PaginatedResponse<PaymentMethod>, PageOptions>({
      query: (data) => ({
        url: `/billing/organizations/my/payment-methods?page=${data.page}&per_page=${data.per_page}`,
      }),
      providesTags: ['Cards'],
    }),

    initiatePaymentMethodSetup: builder.mutation<{ client_secret: string }, void>({
      query: () => ({
        url: '/billing/organizations/my/payment-methods/setup/request',
        method: HTTPRequestType.POST,
      }),
      invalidatesTags: ['Cards'],
    }),

    createPaymentMethod: builder.mutation<Card, { payment_method: string }>({
      query: ({ payment_method }) => ({
        url: '/billing/organizations/my/payment-methods',
        method: HTTPRequestType.POST,
        data: {
          type: 'card',
          service_id: payment_method,
          set_default: true,
        },
      }),
      invalidatesTags: ['Cards'],
    }),

    removePaymentMethod: builder.mutation<APIResponse, string>({
      query: (paymentMethodId) => ({
        url: `/billing/organizations/my/payment-methods/${paymentMethodId}`,
        method: HTTPRequestType.DELETE,
      }),
      invalidatesTags: ['Cards'],
    }),

    setDefaultPaymentMethod: builder.mutation<APIResponse, string>({
      query: (paymentMethodId) => ({
        url: `/billing/organizations/my/payment-methods/${paymentMethodId}/mark-as-default`,
        method: HTTPRequestType.POST,
      }),
      invalidatesTags: ['Cards'],
    }),

    /* Invoices ------------------------------------------------------------ */

    getMyInvoices: builder.query<PaginatedResponse<Invoice>, PageOptions>({
      query: (data) => ({
        url: `/billing/organizations/my/invoices?page=${data.page}&per_page=${data.per_page}`,
      }),
      providesTags: ['Invoices'],
    }),

    payInvoice: builder.mutation<APIResponse, string>({
      query: (invoiceId) => ({
        url: `/billing/organizations/my/invoices/${invoiceId}/pay`,
        method: HTTPRequestType.POST,
      }),
      invalidatesTags: ['Invoices', 'Subscription'],
    }),

    /* Usage --------------------------------------------------------------- */

    getFeatureUsage: builder.query<FeatureUsage[], void>({
      query: () => ({
        url: `/billing/organizations/my/usage`,
      }),
      providesTags: ['Subscription'],
    }),

    getOrgFeatures: builder.query<OrgFeature[], void>({
      query: () => ({
        url: `/billing/organizations/my/features`,
      }),
      providesTags: ['Subscription'],
    }),
  }),
});

export const {
  useGetProductsQuery,
  useGetMySubscriptionQuery,
  useGetMyPaymentMethodsQuery,
  useInitiatePaymentMethodSetupMutation,
  useCreatePaymentMethodMutation,
  useRemovePaymentMethodMutation,
  useLazyGetMyPaymentMethodsQuery,
  useActivateSubscriptionMutation,
  useUpgradeSubscriptionMutation,
  useCancelSubscriptionMutation,
  useGetMyInvoicesQuery,
  useLazyGetMyInvoicesQuery,
  usePayInvoiceMutation,
  useSetDefaultPaymentMethodMutation,
  useGetFeatureUsageQuery,
  useGetOrgFeaturesQuery,
} = billingApi;
